import React, { useContext, useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import { Spinner } from "react-bootstrap";
import { DarkModeContext } from "../../context/DarkModeContext";
import "./CrudTasasAdmin.css";
import { getToken } from "../../utils/auth";


const buildInitialForm = (fields, source = {}) => {
    const next = { id: source.id ?? source.Id ?? 0 };

    fields.forEach((field) => {
        const rawValue = source[field.key];
        if (field.type === "checkbox") {
            next[field.key] = rawValue ?? field.defaultValue ?? false;
            return;
        }

        next[field.key] = rawValue ?? field.defaultValue ?? "";
    });

    return next;
};

const serializeFieldValue = (field, value) => {
    if (field.type === "checkbox") {
        return Boolean(value);
    }

    if (field.type === "number") {
        if (value === "" || value === null || value === undefined) {
            return 0;
        }

        const normalized = String(value).replace(",", ".");
        const numericValue = Number(normalized);
        return Number.isNaN(numericValue) ? 0 : numericValue;
    }

    return value === null || value === undefined ? "" : String(value).trim();
};

const formatFieldValue = (field, value) => {
    if (field.type === "checkbox") {
        return value ? "Si" : "No";
    }

    if (value === null || value === undefined || value === "") {
        return "-";
    }

    if (field.type === "number") {
        const numberValue = Number(value);
        if (Number.isNaN(numberValue)) {
            return value;
        }

        const decimals = typeof field.decimals === "number" ? field.decimals : 2;
        return numberValue.toLocaleString("es-AR", {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals,
        });
    }

    return String(value);
};

const validateForm = (fields, form) => {
    for (const field of fields) {
        const value = form[field.key];

        if (!field.required) {
            continue;
        }

        if (field.type === "checkbox") {
            continue;
        }

        if (value === null || value === undefined || String(value).trim() === "") {
            return `El campo ${field.label} es obligatorio.`;
        }
    }

    return "";
};

const CrudTasasAdmin = ({
    endpointBase,
    entityLabelSingular,
    entityLabelPlural,
    searchPlaceholder,
    fields,
}) => {
    const { darkMode } = useContext(DarkModeContext);
    const token = getToken();

    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [busqueda, setBusqueda] = useState("");
    const [mostrarForm, setMostrarForm] = useState(false);
    const [modo, setModo] = useState("crear");
    const [form, setForm] = useState(() => buildInitialForm(fields));

    const filteredItems = useMemo(() => {
        if (!busqueda.trim()) {
            return items;
        }

        const term = busqueda.toLowerCase();
        return items.filter((item) => {
            const values = [item.id, ...fields.map((field) => item[field.key])];
            return values.some((value) => String(value ?? "").toLowerCase().includes(term));
        });
    }, [busqueda, fields, items]);

    const cargar = async () => {
        if (!token) {
            setError("Token no encontrado");
            return;
        }

        setLoading(true);
        setError("");

        try {
            const response = await fetch(`${endpointBase}/listar`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ Token: token }),
            });

            if (!response.ok) {
                throw new Error("No se pudo cargar el listado.");
            }

            const data = await response.json();
            const normalized = (Array.isArray(data) ? data : []).map((item) => buildInitialForm(fields, item));
            setItems(normalized);
        } catch (err) {
            setItems([]);
            setError(err.message || "No se pudo cargar el listado.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        cargar();
    }, []);

    const abrirCrear = () => {
        setModo("crear");
        setForm(buildInitialForm(fields));
        setMostrarForm(true);
        setError("");
    };

    const abrirEditar = (item) => {
        setModo("editar");
        setForm(buildInitialForm(fields, item));
        setMostrarForm(true);
        setError("");
    };

    const cerrarForm = () => {
        setMostrarForm(false);
        setForm(buildInitialForm(fields));
        setError("");
    };

    const handleFieldChange = (field, event) => {
        const value = field.type === "checkbox" ? event.target.checked : event.target.value;
        setForm((prev) => ({ ...prev, [field.key]: value }));
    };

    const guardar = async () => {
        if (!token) {
            setError("Token no encontrado");
            return;
        }

        const validationError = validateForm(fields, form);
        if (validationError) {
            setError(validationError);
            return;
        }

        setSaving(true);
        setError("");

        try {
            const payload = { Token: token };
            if (modo === "editar") {
                payload.Id = form.id;
            }

            fields.forEach((field) => {
                payload[field.requestKey || field.key] = serializeFieldValue(field, form[field.key]);
            });

            const response = await fetch(`${endpointBase}/${modo === "crear" ? "crear" : "editar"}`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                const text = await response.text();
                throw new Error(text || "No se pudo guardar el registro.");
            }

            await Swal.fire({
                icon: "success",
                title: modo === "crear" ? `${entityLabelSingular} creada` : `${entityLabelSingular} actualizada`,
                timer: 1800,
                showConfirmButton: false,
            });

            cerrarForm();
            await cargar();
        } catch (err) {
            setError(err.message || "No se pudo guardar el registro.");
        } finally {
            setSaving(false);
        }
    };

    const eliminar = async (item) => {
        if (!token) {
            setError("Token no encontrado");
            return;
        }

        const result = await Swal.fire({
            icon: "warning",
            title: `Eliminar ${entityLabelSingular}`,
            text: "Esta accion no se puede deshacer.",
            showCancelButton: true,
            confirmButtonText: "Eliminar",
            cancelButtonText: "Cancelar",
        });

        if (!result.isConfirmed) {
            return;
        }

        setError("");
        try {
            const response = await fetch(`${endpointBase}/eliminar`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ Token: token, Id: item.id }),
            });

            if (!response.ok) {
                const text = await response.text();
                throw new Error(text || "No se pudo eliminar el registro.");
            }

            await Swal.fire({
                icon: "success",
                title: `${entityLabelSingular} eliminada`,
                timer: 1600,
                showConfirmButton: false,
            });

            await cargar();
        } catch (err) {
            setError(err.message || "No se pudo eliminar el registro.");
        }
    };

    return (
        <div className={`admin-tasas-panel ${darkMode ? "is-dark" : ""}`}>
            <div className="admin-tasas-toolbar">
                <div className="admin-tasas-toolbar-actions">
                    <button className="btn btn-dark" onClick={abrirCrear}>
                        Nueva {entityLabelSingular}
                    </button>
                    <button className="btn btn-outline-dark" onClick={cargar} disabled={loading}>
                        {loading ? "Actualizando..." : "Actualizar"}
                    </button>
                </div>

                <div className="admin-tasas-search-wrap">
                    <input
                        className="form-control"
                        type="text"
                        value={busqueda}
                        onChange={(event) => setBusqueda(event.target.value)}
                        placeholder={searchPlaceholder}
                    />
                </div>
            </div>

            {error && <div className="alert alert-danger mb-3">{error}</div>}

            {mostrarForm && (
                <div className={`admin-tasas-form-card ${darkMode ? "is-dark" : ""}`}>
                    <div className="admin-tasas-form-header">
                        <div>
                            <h5 className="m-0">{modo === "crear" ? `Nueva ${entityLabelSingular}` : `Editar ${entityLabelSingular}`}</h5>
                            {modo === "editar" && <span className="admin-tasas-form-id">ID {form.id}</span>}
                        </div>
                    </div>

                    <div className="admin-tasas-grid">
                        {fields.map((field) => (
                            <div
                                key={field.key}
                                className={`admin-tasas-field ${field.type === "checkbox" ? "is-checkbox" : ""} ${field.fullWidth ? "is-full" : ""}`}
                            >
                                <label className="admin-tasas-label">{field.label}</label>

                                {field.type === "select" ? (
                                    <select
                                        className="form-select"
                                        value={form[field.key]}
                                        onChange={(event) => handleFieldChange(field, event)}
                                    >
                                        {(field.options || []).map((option) => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </select>
                                ) : field.type === "checkbox" ? (
                                    <label className="admin-tasas-checkbox-wrap">
                                        <input
                                            type="checkbox"
                                            checked={Boolean(form[field.key])}
                                            onChange={(event) => handleFieldChange(field, event)}
                                        />
                                        <span>{Boolean(form[field.key]) ? "Activo" : "Inactivo"}</span>
                                    </label>
                                ) : (
                                    <input
                                        className="form-control"
                                        type={field.type === "number" ? "number" : "text"}
                                        step={field.step || (field.type === "number" ? "0.01" : undefined)}
                                        value={form[field.key]}
                                        onChange={(event) => handleFieldChange(field, event)}
                                        placeholder={field.placeholder || ""}
                                    />
                                )}
                            </div>
                        ))}
                    </div>

                    <div className="admin-tasas-form-actions">
                        <button className="btn btn-dark" onClick={guardar} disabled={saving}>
                            {saving ? "Guardando..." : modo === "crear" ? "Guardar" : "Actualizar"}
                        </button>
                        <button className="btn btn-outline-secondary" onClick={cerrarForm} disabled={saving}>
                            Cancelar
                        </button>
                    </div>
                </div>
            )}

            <div className="admin-tasas-summary">
                <span>{entityLabelPlural}: {filteredItems.length}</span>
            </div>

            <div className="admin-tasas-table-wrap">
                {loading ? (
                    <div className="admin-tasas-loading">
                        <Spinner animation="border" size="sm" />
                        <span>Cargando datos...</span>
                    </div>
                ) : (
                    <table className="admin-tasas-table">
                        <thead>
                            <tr>
                                <th>ID</th>
                                {fields.map((field) => (
                                    <th key={field.key}>{field.label}</th>
                                ))}
                                <th>Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredItems.length > 0 ? (
                                filteredItems.map((item) => (
                                    <tr key={item.id}>
                                        <td>{item.id}</td>
                                        {fields.map((field) => (
                                            <td key={`${item.id}-${field.key}`}>{formatFieldValue(field, item[field.key])}</td>
                                        ))}
                                        <td>
                                            <div className="admin-tasas-actions">
                                                <button className="btn btn-sm btn-dark" onClick={() => abrirEditar(item)}>
                                                    Editar
                                                </button>
                                                <button className="btn btn-sm btn-outline-danger" onClick={() => eliminar(item)}>
                                                    Eliminar
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={fields.length + 2} className="admin-tasas-empty">
                                        No hay resultados para mostrar.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
};

export default CrudTasasAdmin;
